import { LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";
import { ILanguageExtended } from "@multiweb/types/contracts";
import { CaseInsensitiveMap } from "../../../../Global/Base/DataTypes/CaseInsensitiveMap";
import { Event as GlobalEvent } from "../../../../Global/Base/Events";
import { IDisposable } from "../../../../Global/Base/IDisposable";
import { OWObject } from "../../../../OW.Remoting/Remoting/DataTypes/OWObject";
import { ActionContext, ActionType, IAction } from "../../../UI.Controls/Actions/IAction";
import { MenuType } from "../../../UI.Controls/Actions/MenuType";
import { IDropDownMenuItem } from "../../../UI.Controls/DropDownMenu/DropDownInterfaces";
import { DropDownMenu } from "../../../UI.Controls/DropDownMenu/DropDownMenu";
import { DropDownMenuComboItem } from "../../../UI.Controls/DropDownMenu/DropDownMenuComboItem";
import { DropDownMenuItem } from "../../../UI.Controls/DropDownMenu/DropDownMenuItem";
import { DropDownMenuItemButton } from "../../../UI.Controls/DropDownMenu/DropDownMenuItemButton";
import { DropDownMenuLoader } from "../../../UI.Controls/DropDownMenu/DropDownMenuLoader";
import { DropDownMenuWithSubmenuItem } from "../../../UI.Controls/DropDownMenu/DropDownMenuWithSubmenuItem";
import { SeparatorMenuItem } from "../../../UI.Controls/DropDownMenu/SeparatorItem";
import { NodeAction, NodeActionType } from "../XGControls/Actions/NodeAction";
import { ReportAction } from "../XGControls/Actions/ReportAction";
import { StandardAction } from "../XGControls/Actions/StandardAction";
import { StandardRecordAction } from "../XGControls/Actions/StandardRecordAction";
import { IFrame } from "../XGControls/IFrame";
import { Icon, MenuTypes } from "./FormUI";
import { ToolbarActionPrescription } from "./FormUIShared";
import { FormUIStandard } from "./FormUIStandard";

/**
     * Akce objektu, která má být vyrenderována do dropdown menu
     */
class DropDownActionItem implements IDisposable {
    /**Vnořené akce*/
    public nestedActions: DropDownActionItem[] = [];

    private _action: IAction;

    constructor(action: IAction, public readonly icon: string | Icon) {
        this._action = action;
    }

    public get action(): IAction {
        return this._action;
    }

    /**Vrací informaci, jestli má být uzel viditelný.*/
    public get isVisible(): boolean {
        if (this.action instanceof NodeAction || this.nestedActions.length > 0)
            return this.nestedActions.some(a => a.isVisible && a.action.context === ActionContext.objectAction); // Pokud není viditelná žádná akce pro objekt, tak by se menu nemělo být viditelné.

        return this.action && this.action.visible;
    }

    public dispose(): void {
        this.nestedActions = [];
    }
}

const logger = LazyLoadingSectionLogger.create(LoggingSection.formUI);

export class ObjectActionsMenu implements IDisposable {
    /***Událost, která nastane při otevření menu akcí*/
    public readonly opened: GlobalEvent<ObjectActionsMenu, OWObject> = new GlobalEvent<ObjectActionsMenu, OWObject>();
    /***Událost, která nastane při zavření menu akcí*/
    public readonly closed: GlobalEvent<ObjectActionsMenu, void> = new GlobalEvent<ObjectActionsMenu, void>();
    /***Událost, která nastane při kliknutí myší na položku v menu akcí.*/
    public readonly itemSelected: GlobalEvent<IAction, MouseEvent> = new GlobalEvent<IAction, MouseEvent>();

    /*Kolekce akcí, které jsou kořenové uzly nebo nemají nastavený masterNode*/
    private _addedActions: DropDownActionItem[] = [];
    /**Kolekce akcí, které mouhou být použity jako masterNode (musí mít ctrlID).*/
    private _addedActionsWithCtrlId: CaseInsensitiveMap<DropDownActionItem> = new CaseInsensitiveMap<DropDownActionItem>();
    /**Kolekce objektů, které doposud nebyly přidány, protože čekají na přidání svého master node*/
    private _waitingStack: Array<ToolbarActionPrescription> = new Array<ToolbarActionPrescription>();
    private _dropDownMenu: DropDownMenu;
    private _adjustStandardActionsRightsMethod: (actions: IAction[]) => void;
    private __getLanguages: () => Promise<readonly ILanguageExtended[]>;
    private _lastShowEvent: Event;

    protected get _getLanguages(): () => Promise<readonly ILanguageExtended[]> {
        return this.__getLanguages;
    }

    public get isShown(): boolean {
        return this._dropDownMenu && this._dropDownMenu.isOpened;
    }

    public getActions<T extends IAction>(): Iterable<T> {
        return this._getIterator<T>();
    }

    private *_getIterator<T extends IAction>(): IterableIterator<T> {
        for (const a of this._addedActions.sort(function(a, b) {
            if (a.action.priority === b.action.priority)
                return a.action.order - b.action.order;
            else
                return a.action.priority - b.action.priority;
        })) yield a.action as T;
    }

    private _findMasterNode(ctrlID: string): DropDownActionItem {
        return this._addedActionsWithCtrlId.has(ctrlID) ? this._addedActionsWithCtrlId.get(ctrlID) : null;
    }

    public remove(action: IAction): void {
        if (action.masterNode) {
            const masterNode = this._findMasterNode(action.masterNode);
            if (masterNode) {
                masterNode.nestedActions = masterNode.nestedActions.filter(i => i.action !== action);
            } else {
                // pokud neexistuje masterNode, prohledam cekajici akce, jestli neni mezi nema a odstranim ji z pole
                this._waitingStack = this._waitingStack.filter(i => i.action !== action);
            }
        } else {
            this._addedActions = this._addedActions.filter(i => i.action !== action);
        }
    }

    /**
     * @param getLanguages Funkce umožňující načtení všech dostupných jazyků.
     * @param adjustStandardActionsRightsMethod
     */
    constructor(getLanguages: () => Promise<readonly ILanguageExtended[]>, adjustStandardActionsRightsMethod?: (stdActions: IAction[]) => void) {
        this.__getLanguages = getLanguages;
        this._adjustStandardActionsRightsMethod = adjustStandardActionsRightsMethod;
    }

    public add(action: IAction): void {
        //Pokud je nova akce zalozka tak ji rovnou pridam jako skupinu
        if (action instanceof NodeAction && action.nodeActionType == NodeActionType.naPage) {
            action.nodeActionType = NodeActionType.naGroup;
        }

        const menuAction = new DropDownActionItem(action, action.icon);

        if (action.masterNode) { //pokud ma masternode.
            //Pokusí se najít masterNode, pokud existuje, tak se mu přidá jako potomek. Pokud zatím nebyl master přidán, tak se akce zařadí do zásobníku a bude čekat na přidání master node.    let masterNode = this._findMasterNode(action.masterNode);
            const masterNode = this._findMasterNode(action.masterNode);
            if (masterNode) {
                masterNode.nestedActions.push(menuAction);
            } else {
                this._waitingStack.push(new ToolbarActionPrescription(MenuTypes.contextMenu, action));
            }
        } else {
            menuAction.action.order = this._addedActions.length + 1;
            this._addedActions.push(menuAction);
        }

        if (action instanceof NodeAction) {
            //byla přidána nová action, která by mohla být něčí masterNode. Projdou se akce čekající na masterNode a pokusí se najít shoda.
            if (action.ctrlID)
                this._addedActionsWithCtrlId.set(action.ctrlID, menuAction);
            this._tryAddFromWaitingStack(action);
        }
    }

    private _tryAddFromWaitingStack(parent: NodeAction): void {
        const toRemove: ToolbarActionPrescription[] = [];
        for (const a of this._waitingStack) {
            if (a.action.masterNode === parent.ctrlID) {
                this.add(a.action);
                toRemove.push(a);
            }
        }

        for (const d of toRemove) {
            const index = this._waitingStack.indexOf(d);
            if (index > -1) {
                this._waitingStack.splice(index, 1);
            }
        }
    }

    /**
     * Zobrazí menu akcí vzhledem k vybranému objektu.
     */
    public show(element: HTMLElement, e: Event, ao: OWObject, _isObjInEdit: boolean, frame: IFrame): void {
        this._dropDownMenu = this._render(ao, frame);
        if (e) {
            this._lastShowEvent = e;
        } else {
            e = this._lastShowEvent;
        }
        this._dropDownMenu.show(element, e, (e instanceof MouseEvent || e instanceof KeyboardEvent) && e.detail === 0);
        this.opened.trigger(this, ao);
        this._dropDownMenu.onClose.add(() => this.closed.trigger(this, undefined));
    }

    /**
     * Zobrazí prázdné dropdownmenu s loaderem.
     */
    public showEmpty(element: HTMLElement, e: Event): void {
        this._lastShowEvent = e;
        if (this._dropDownMenu)
            this._dropDownMenu.close();
        this._dropDownMenu = this._renderEmpty();
        this._dropDownMenu.show(element, e);
        this._dropDownMenu.onClose.add(() => this.closed.trigger(this, undefined));
    }

    public close(): void {
        if (this._dropDownMenu)
            this._dropDownMenu.close();
        this.closed.trigger(this, undefined);
    }

    private _renderEmpty(): DropDownMenu {
        const actionsMenu = new DropDownMenu();
        actionsMenu.classList.add("form-ui-standard", "loading");
        actionsMenu.addItem(DropDownMenuLoader.createMenuLoader("GUI-638298")); //Prosím čekejte
        return actionsMenu;
    }

    private _render(ao: OWObject, _frame: IFrame): DropDownMenu {
        const actionsMenu = new DropDownMenu();
        actionsMenu.classList.add("form-ui-standard", "actions", "loaded");
        let prevGroupSize = 0;

        /**vyrenderuje skupinu akcí*/
        const renderGroupFunc = (collection: DropDownActionItem[]) => {
            collection = collection.filter(i => i.isVisible).sort((a, b) => a.action.priority - b.action.priority);
            if (prevGroupSize > 0 && collection.length > 0)
                actionsMenu.addItem(new SeparatorMenuItem());

            for (const a of collection) {
                renderItemFunc(a);
            }
            if (collection.length > 0)
                prevGroupSize = collection.length;
        };

        /**vyrenderuje položku menu*/
        const renderItemFunc = (a: DropDownActionItem, parent?: DropDownMenu) => {
            const icon: string = typeof a.icon === "number" ? Icon[a.icon] : a.icon;
            const isGroup = (!(a.action instanceof StandardAction) && a.action instanceof NodeAction && a.action.nodeActionType == NodeActionType.naGroup);

            if (a.action instanceof NodeAction && !a.isVisible)
                return;

            let item: IDropDownMenuItem;
            if (a.nestedActions.length > 0) {
                const menu = new DropDownMenu();
                item = DropDownMenuWithSubmenuItem.createDropSubMenuItem(icon, a.action.locKey, menu, a.action.priority, a.action.toMenu);
                for (const child of a.nestedActions)
                    renderItemFunc(child, menu);
            } else {
                //StandardRecordAction je zamýšlen jako samostatné tlačítko. Nemá vyplěn "action". Místo toho se spouští recordAction pomocí události "click" nad htmlElementem.
                //proto se vytvoří umělý action, který zavolá recordAction pro vybraný objekt.
                if (a.action instanceof StandardRecordAction)
                    // @ts-ignore Vyřešit v rámci bugu https://tfs.tescosw.loc/Jadro/Jadro%20.NET/_workitems/edit/32275
                    a.action.action = () => (a.action as StandardRecordAction).recordAction(null, ao.id);
                if (a.action instanceof ReportAction && a.action.showSettings) {
                    const settings = FormUIStandard.createMenuReportSettings(a.action, Icon.settings, MenuTypes.contextMenu, undefined, this._getLanguages);
                    item = DropDownMenuComboItem.createDropDownMenuComboItem(icon, a.action, a.action.locKey, settings);
                } else
                    item = DropDownMenuItemButton.createDropMenuItemButton(icon, a.action, a.action.locKey, a.action.enabled);

                item.addEventListener("click", (e: MouseEvent) => {
                    this.itemSelected.trigger(a.action, e);
                    this._dropDownMenu.visible = false;
                });
            }

            if (a.action.id)
                item.setAttribute("id", a.action.id + "-da");
            if (a.action.locKey)
                item.setAttribute("data-lockey", a.action.locKey);
            item.setAttribute("data-action-type", ActionType[a.action.actionType]);

            if (isGroup && parent)
                actionsMenu.addItem(new SeparatorMenuItem());

            if (!parent)
                actionsMenu.addItem(item);
            else if (parent)
                parent.addItem(item);
        };

        renderGroupFunc.bind(this);
        renderItemFunc.bind(this);

        const menuGroups: Map<MenuType, DropDownActionItem[]> = new Map<MenuType, DropDownActionItem[]>([
            [MenuType.mtRecordNavigation, []],
            [MenuType.mtStandardAction, []],
            [MenuType.mtActions, []],
            [MenuType.mtForm, []],
            [MenuType.mtWorkflow, []],
            [MenuType.mtNone, []],
            [MenuType.mtPrint, []],
            [MenuType.mtConditionForm, []],
            [MenuType.mtHelp, []],
            [MenuType.mtInfo, []],
        ]);

        const newActionGroups: DropDownActionItem[] = [];

        //rozřadí akce do skupin
        for (const a of this._addedActions) {
            if (a.action.actionType === ActionType.standardOpenDetail //tlačítko "i" (detail záznamu) nemá být ui-standart renderováno do drop down menu.
                || a.action.actionType === ActionType.standardSelectRecord //tlačítko "vybrat" nebude renderováno do drop down menu.
                || a.action.actionType === ActionType.standardRowActions //tlačítko "..." nebude renderováno do drop down menu.
                || a.action.actionType === ActionType.standardMoveRightRecord // tlačítko "přidat" v TLF
                || a.action.actionType === ActionType.standardMoveLeftRecord) { // tlačítko "odebrat" v TLF
                continue;
            }

            if (a.action.toMenu === MenuType.mtStandardAction || a.action.toMenu === MenuType.mtCancelSave)
                menuGroups.get(MenuType.mtStandardAction).push(a);
            else if (a.action.toMenu === MenuType.mtActions ||
                a.action.toMenu === MenuType.mtForm ||
                a.action.toMenu === MenuType.mtWorkflow ||
                a.action.toMenu === MenuType.mtNone ||
                a.action.toMenu === MenuType.mtPrint ||
                a.action.toMenu === MenuType.mtConditionForm ||
                a.action.toMenu === MenuType.mtHelp ||
                a.action.toMenu === MenuType.mtInfo)
                menuGroups.get(a.action.toMenu).push(a);
            else if (a.action.toMenu === MenuType.mtRecordNavigation)
                menuGroups.get(MenuType.mtRecordNavigation).push(a);
            else if (a.action instanceof NodeAction && a.action.nodeActionType == NodeActionType.naGroup)
                newActionGroups.push(a);
            else
                logger.log(`unknown MenuType ${a.action.toMenu}`);
        }
        //pokud je zadána metoda, zavolá se pro úpravu práv standartních akcí
        if (this._adjustStandardActionsRightsMethod && menuGroups.get(MenuType.mtStandardAction).length > 0)
            this._adjustStandardActionsRightsMethod(menuGroups.get(MenuType.mtStandardAction).map(i => i.action));

        //vyrenderuje jednotlivé skupiny
        renderGroupFunc(menuGroups.get(MenuType.mtRecordNavigation));
        renderGroupFunc(menuGroups.get(MenuType.mtStandardAction).filter(a => a.action.actionType !== ActionType.standardDeleteRecord));
        renderGroupFunc(menuGroups.get(MenuType.mtStandardAction).filter(a => a.action.actionType === ActionType.standardDeleteRecord));
        renderGroupFunc(menuGroups.get(MenuType.mtActions));
        renderGroupFunc(menuGroups.get(MenuType.mtWorkflow));
        renderGroupFunc(menuGroups.get(MenuType.mtForm));
        renderGroupFunc(menuGroups.get(MenuType.mtConditionForm));
        renderGroupFunc(menuGroups.get(MenuType.mtNone));
        for (const ng of newActionGroups)
            renderGroupFunc([ng]);
        renderGroupFunc(menuGroups.get(MenuType.mtPrint));
        renderGroupFunc(menuGroups.get(MenuType.mtInfo));
        renderGroupFunc(menuGroups.get(MenuType.mtHelp));

        if (actionsMenu.itemsCount === 0) //objekt nemá žádné aktivní akce
            actionsMenu.addItem(DropDownMenuItem.createDropMenuItem("food-cup", () => undefined, "GUI-639188", false));

        return actionsMenu;
    }

    public dispose(): void {
        this.opened.removeAll();
        this.closed.removeAll();
        this.itemSelected.removeAll();
        this._addedActions?.forEach((item) => item.dispose());
        this._addedActions = [];
        this._addedActionsWithCtrlId.clear();
        this._waitingStack = [];
        this._dropDownMenu?.dispose();
    }
}
